import React, { useEffect, useMemo, useState } from "react";
import config from "../configLoader";
import AccountCodeManager from "../managers/AccountCodeManager";
import { AccountCode } from "../interfaces/AccountCodeInterface";
import {
  getUpdateMiscEmployeeTaskErrorMessage,
  updateMiscEmployeeTaskAccountCodeAndDescription,
} from "../api/miscEmployeeTask";
import { useSiteContext } from "../SiteContext";

const DESCRIPTION_MAX = 500;

export interface UpdateMiscEmployeeTaskModalProps {
  open: boolean;
  onClose: () => void;
  miscEmployeeTaskID: number;
  initialAccountCodeID: number | null;
  initialMiscDescription: string;
  onSuccess: () => void | Promise<void>;
}

const UpdateMiscEmployeeTaskModal: React.FC<UpdateMiscEmployeeTaskModalProps> = ({
  open,
  onClose,
  miscEmployeeTaskID,
  initialAccountCodeID,
  initialMiscDescription,
  onSuccess,
}) => {
  const { createPopup, translator } = useSiteContext();
  const [accountCodes, setAccountCodes] = useState<AccountCode[]>([]);
  const [selectedAccountCodeId, setSelectedAccountCodeId] = useState<number | null>(initialAccountCodeID);
  const [description, setDescription] = useState(initialMiscDescription);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  const activeAccountCodes = useMemo(
    () => accountCodes.filter((c) => c.active).sort((a, b) => a.name.localeCompare(b.name)),
    [accountCodes]
  );

  /** Include current code in the list when it is inactive so the select stays valid. */
  const displayAccountCodes = useMemo(() => {
    if (initialAccountCodeID == null) {
      return activeAccountCodes;
    }
    if (activeAccountCodes.some((c) => c.accountCodeID === initialAccountCodeID)) {
      return activeAccountCodes;
    }
    const current = accountCodes.find((c) => c.accountCodeID === initialAccountCodeID);
    if (!current) {
      return activeAccountCodes;
    }
    return [current, ...activeAccountCodes];
  }, [activeAccountCodes, accountCodes, initialAccountCodeID]);

  useEffect(() => {
    if (!open) {
      return;
    }
    setSelectedAccountCodeId(initialAccountCodeID);
    setDescription(initialMiscDescription.slice(0, DESCRIPTION_MAX));
    setLoadError(null);
    setSaveError(null);
    setSaving(false);

    let cancelled = false;
    (async () => {
      try {
        const codes = await AccountCodeManager.getAccountCodes();
        if (!cancelled) {
          setAccountCodes(codes || []);
        }
      } catch {
        if (!cancelled) {
          setLoadError(translator.translate("UpdateMiscTaskLoadAccountCodesFailed"));
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [open, miscEmployeeTaskID, initialAccountCodeID, initialMiscDescription, translator]);

  const accountChanged = selectedAccountCodeId !== initialAccountCodeID;
  const descriptionChanged = description !== initialMiscDescription;
  const hasChanges = accountChanged || descriptionChanged;
  const canSave = hasChanges && !saving && !loadError;

  const handleSave = async () => {
    if (!canSave) {
      return;
    }

    const body: {
      miscEmployeeTaskID: number;
      accountCodeID?: number;
      miscDescription?: string;
    } = { miscEmployeeTaskID };

    if (accountChanged && selectedAccountCodeId !== null) {
      body.accountCodeID = selectedAccountCodeId;
    }

    if (descriptionChanged) {
      body.miscDescription = description.slice(0, DESCRIPTION_MAX);
    }

    const sendsAccount = accountChanged && selectedAccountCodeId !== null;
    if (!sendsAccount && !descriptionChanged) {
      setSaveError(translator.translate("UpdateMiscTaskNoChanges"));
      return;
    }

    setSaving(true);
    setSaveError(null);
    try {
      await updateMiscEmployeeTaskAccountCodeAndDescription(body);
      await onSuccess();
      onClose();
    } catch (err: unknown) {
      const status = (err as { response?: { status?: number } })?.response?.status;
      const msg = getUpdateMiscEmployeeTaskErrorMessage(err);
      if (status === 403) {
        createPopup(
          translator.translate("UpdateMiscTaskModalTitle"),
          translator.translate("UpdateMiscTaskForbidden")
        );
      } else {
        setSaveError(msg);
      }
    } finally {
      setSaving(false);
    }
  };

  if (!open) {
    return null;
  }

  return (
    <div
      style={{
        position: "fixed",
        inset: 0,
        backgroundColor: "rgba(0,0,0,0.5)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        zIndex: 1000,
      }}
      onClick={onClose}
    >
      <div
        style={{
          background: "#fff",
          padding: 24,
          borderRadius: 8,
          minWidth: 380,
          maxWidth: "min(520px, 92vw)",
          boxShadow: "0 4px 20px rgba(0,0,0,0.15)",
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <h3 style={{ marginTop: 0 }}>{translator.translate("UpdateMiscTaskModalTitle")}</h3>

        {loadError && (
          <p style={{ color: "#c00", marginBottom: 12 }}>{loadError}</p>
        )}

        <div style={{ marginBottom: 14 }}>
          <label style={{ display: "block", marginBottom: 6, fontWeight: 600 }}>
            {translator.translate("UpdateMiscTaskAccountCode")}
          </label>
          <select
            value={selectedAccountCodeId === null ? "" : String(selectedAccountCodeId)}
            onChange={(e) => {
              const v = e.target.value;
              setSelectedAccountCodeId(v === "" ? null : parseInt(v, 10));
            }}
            disabled={!!loadError}
            style={{
              width: "100%",
              padding: 8,
              backgroundColor: config.secondaryColor,
              border: "1px solid #ccc",
              borderRadius: 4,
            }}
          >
            {initialAccountCodeID === null && (
              <option value="">{translator.translate("UpdateMiscTaskAccountPlaceholder")}</option>
            )}
            {displayAccountCodes.map((code) => (
              <option key={code.accountCodeID} value={String(code.accountCodeID)}>
                {code.name} ({code.altAccountCodeID})
              </option>
            ))}
          </select>
        </div>

        <div style={{ marginBottom: 16 }}>
          <label style={{ display: "block", marginBottom: 6, fontWeight: 600 }}>
            {translator.translate("UpdateMiscTaskDescription")}
          </label>
          <textarea
            value={description}
            maxLength={DESCRIPTION_MAX}
            onChange={(e) => setDescription(e.target.value.slice(0, DESCRIPTION_MAX))}
            disabled={!!loadError}
            rows={4}
            style={{
              width: "100%",
              boxSizing: "border-box",
              padding: 8,
              backgroundColor: config.secondaryColor,
              border: "1px solid #ccc",
              borderRadius: 4,
              resize: "vertical",
            }}
          />
          <div style={{ fontSize: 12, color: "#555", marginTop: 4 }}>
            {description.length} / {DESCRIPTION_MAX}
          </div>
        </div>

        {saveError && (
          <p style={{ color: "#c00", marginBottom: 12 }}>{saveError}</p>
        )}

        <div style={{ display: "flex", justifyContent: "flex-end", gap: 10 }}>
          <button type="button" onClick={onClose} disabled={saving}>
            {translator.translate("UpdateMiscTaskCancel")}
          </button>
          <button type="button" onClick={handleSave} disabled={!canSave}>
            {saving ? translator.translate("LoadingData") : translator.translate("UpdateMiscTaskSave")}
          </button>
        </div>
      </div>
    </div>
  );
};

export default UpdateMiscEmployeeTaskModal;
